import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"
import styled from "styled-components"

import {
    CcReviewReviewItem,
    CcReviewReviewItemState,
    CcReviewReviewHistory,
    CcReviewReviewHistoryImportProps,
    CcReviewReviewRevision,
    CcReviewReviewTempAnswer,
} from "server/models"
import {
    IDLE, PENDING, EDIT,
    WORKFLOW_RERUN,
    DESC,
} from "const"
import { StateType, CcStateSettingsType, hasProjectAccess, deepCopy } from "client/utils"
import { sort } from "client/store/utils"
import {
    PRM_CC_REVIEW,
    StoreType,
    ReviewReviewItemList,
    ReviewReviewRevisionValidation,

    LoadCcReviewReviewRevisions,
    LoadCcReviewReviewItemState,
    LoadCcReviewReviewHistory,
    LoadCcReviewReviewItemRevisionData,

    CreateCcReviewReviewState,
    ImportCcReviewReviewHistory,
    CreateCcReviewReviewRevision,
    UpdateCcReviewReviewRevision,
    SaveCcReviewReviewTempAnswer,
    DeleteCcReviewReviewRevision,
    DeleteCcReviewReviewItem,

    setCcReviewReviewRevisionList,
    setCcReviewReviewRevisionActive,
    setCcReviewReviewItemIds,
    setCcReviewReviewItemList,
    setCcReviewReviewLoadState,

    initReviewReviewHistory,
} from "client/store"
import {
    SelectDropdown,
    SelectDropdownItem,
    FormGroup,
    Label,
    Input,
    Feedback,
    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,

    PrepareNestedHistory,
} from "client/components"


const ItemRevisionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    & > ${Button} {
        margin-bottom: 0;
    }
`


interface ReviewItemRevisionListProps {
    item: CcReviewReviewItem
}

const ReviewItemRevisionList = ({
    item,
}: ReviewItemRevisionListProps) => {
    const itemList = useSelector((state: StoreType) => state.ccReviewReview.itemList)
    const itemIds = useSelector((state: StoreType) => state.ccReviewReview.itemIds)
    const revisionList = useSelector((state: StoreType) => state.ccReviewReview.revisionList)
    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewReview.revisionActiveList)

    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[2] || [] // review level ids

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [revisions, setRevisions] = useState<CcReviewReviewRevision[]>([])

    const [showDelete, setShowDelete] = useState(false)
    const [showRename, setShowRename] = useState(false)

    const [revisionName, setRevisionName] = useState("")
    const [revisionValidation, setRevisionValidation] = useState<ReviewReviewRevisionValidation>(null)
    const [renameState, setRenameState] = useState<StateType>(IDLE)

    const activeRevision = revisionActiveList[item.id] || null
    const editAccess = hasProjectAccess(PRM_CC_REVIEW, EDIT)

    useEffect(() => {
        // display the last revision on top
        const revisionArray: CcReviewReviewRevision[] = revisionList[item.id] ? revisionList[item.id].slice() : []
        revisionArray.sort((a, b) => sort(a, b, "number", DESC))
        setRevisions(revisionArray)
    }, [revisionList])

    const changeActiveRevision = (revision: CcReviewReviewRevision) => {
        dispatch(setCcReviewReviewLoadState(PENDING))
        dispatch(setCcReviewReviewRevisionActive(item.id, revision))

        LoadCcReviewReviewItemRevisionData(item.id, revision.id, dispatch).then(() => {
            dispatch(setCcReviewReviewLoadState(IDLE))
        })
    }

    const onCancelDelete = () => {
        setShowDelete(false)
    }
    const onConfirmDelete = () => {
        // Delete revision. If this is the only revision - remove review item
        setShowDelete(false)
        dispatch(setCcReviewReviewLoadState(PENDING))

        DeleteCcReviewReviewRevision(activeRevision.id).then(() => {
            LoadCcReviewReviewRevisions(item.id).then((newRevisions) => {
                if (newRevisions.length > 0) {
                    // set new revision list and load revision data
                    const lastRevision = newRevisions[newRevisions.length - 1]
                    dispatch(setCcReviewReviewRevisionList(item.id, newRevisions))
                    dispatch(setCcReviewReviewRevisionActive(item.id, lastRevision))

                    LoadCcReviewReviewItemRevisionData(item.id, lastRevision.id, dispatch).then(() => {
                        dispatch(setCcReviewReviewLoadState(IDLE))
                    })
                } else {
                    // remove item
                    DeleteCcReviewReviewItem(item.id).then(() => {
                        const newItemIds = itemIds.filter((itemId) => itemId !== item.id)
                        const newItemList: ReviewReviewItemList = deepCopy(itemList)
                        delete newItemList[item.id]

                        dispatch(setCcReviewReviewItemIds(newItemIds))
                        dispatch(setCcReviewReviewItemList(newItemList))
                        dispatch(setCcReviewReviewLoadState(IDLE))
                    })
                }
            })
        })
    }

    const onCancelRename = () => {
        setShowRename(false)
    }
    const onConfirmRename = () => {
        setRenameState(PENDING)
        UpdateCcReviewReviewRevision(activeRevision.id, revisionName, lang).then((validation) => {
            if (validation) {
                setRevisionValidation(validation)
                setRenameState(IDLE)
            } else {
                LoadCcReviewReviewRevisions(item.id).then((newRevisions) => {
                    const newActiveRevision = newRevisions.find((row) => row.id === activeRevision.id)
                    dispatch(setCcReviewReviewRevisionList(item.id, newRevisions))
                    dispatch(setCcReviewReviewRevisionActive(item.id, newActiveRevision))

                    setRenameState(IDLE)
                    setShowRename(false)
                })
            }
        })
    }

    const onRerun = () => {
        dispatch(setCcReviewReviewLoadState(PENDING))

        LoadCcReviewReviewRevisions(item.id).then((resRevisions) => {
            CreateCcReviewReviewRevision(item.id)
            .then((newRevision) => {
                const lastRevision = resRevisions[resRevisions.length - 1]

                Promise.all([
                    LoadCcReviewReviewItemState(item.id, lastRevision.id),
                    LoadCcReviewReviewHistory(item.id, lastRevision.id),
                ]).then(([itemState, itemHistory]) => {
                    const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}
                    const promiseArray: Promise<any>[] = []

                    let lastHistory: CcReviewReviewHistory = null
                    let lastResponsibleAnswer: CcReviewReviewHistory = null
                    for (let i = itemHistory.length - 1; i >= 0; i--) {
                        const history = itemHistory[i]
                        if (history.workflow === WORKFLOW_RERUN) {
                            break
                        }
                        if (!history.systemMessage) {
                            if (!lastHistory) lastHistory = history
                            if (!lastResponsibleAnswer && history.parentId === null) lastResponsibleAnswer = history
                        }
                        if (lastHistory && lastResponsibleAnswer) break
                    }

                    const newHistory: CcReviewReviewHistory = {
                        ...initReviewReviewHistory,
                        id: null,
                        itemId: item.id,
                        reviewReviewRevisionId: newRevision.id,
                        parentId: null,
                        workflow: WORKFLOW_RERUN,
                        systemMessage: true,
                        editorId: userData.userId,
                    }
                    const newState: CcReviewReviewItemState = {
                        ...itemState,
                        reviewReviewRevisionId: newRevision.id,
                        levelId: levelIds[0] || null,
                        verifierId: (levelIds[0] && settings[levelIds[0]]) || null,
                    }

                    itemHistory.push(newHistory)

                    if (lastResponsibleAnswer) {
                        const tempAnswer: CcReviewReviewTempAnswer = {
                            itemId: item.id,
                            reviewReviewRevisionId: newRevision.id,
                            editorId: lastResponsibleAnswer.prevUserId,
                            responseId: lastResponsibleAnswer.responseId,
                            remark: lastResponsibleAnswer.remark,
                            nextRevision: lastResponsibleAnswer.nextRevision,
                            nextUserId: lastResponsibleAnswer.nextUserId,
                        }
                        promiseArray.push(
                            SaveCcReviewReviewTempAnswer(tempAnswer, lang),
                        )
                    }

                    const nestedHistory = PrepareNestedHistory(itemHistory)

                    const importData: CcReviewReviewHistoryImportProps = {
                        nestedHistory,
                        newRevisionId: newRevision.id,
                    }
                    promiseArray.push(
                        ImportCcReviewReviewHistory(importData),
                        CreateCcReviewReviewState(newState),
                    )

                    Promise.all(promiseArray).then(() => {
                        // reload data
                        LoadCcReviewReviewRevisions(item.id)
                        .then((newRevisions) => {
                            const newLastRevision = newRevisions[newRevisions.length - 1]
                            dispatch(setCcReviewReviewRevisionList(item.id, newRevisions))
                            dispatch(setCcReviewReviewRevisionActive(item.id, newLastRevision))

                            LoadCcReviewReviewItemRevisionData(item.id, newLastRevision.id, dispatch).then(() => {
                                dispatch(setCcReviewReviewLoadState(IDLE))
                            })
                        })
                    })
                })
            })
        })
    }


    return (
        <ItemRevisionWrapper onClick={(e) => e.stopPropagation()}>
            <SelectDropdown
                title={activeRevision ? activeRevision.name : ""}
                row="true"
                style={{ width: "120px" }}
            >
                {
                    revisions.map((revision) => (
                        <SelectDropdownItem
                            key={revision.id}
                            onClick={() => changeActiveRevision(revision)}
                        >
                            {revision.name}
                        </SelectDropdownItem>
                    ))
                }
            </SelectDropdown>
            {
                editAccess && (
                    <>
                        <Button
                            row
                            onClick={() => setShowDelete(true)}
                        >
                            {lang.delete}
                        </Button>
                        <Button
                            row
                            variant="success"
                            onClick={() => {
                                setRevisionName(activeRevision.name)
                                setShowRename(true)
                            }}
                        >
                            {lang.rename}
                        </Button>
                        <Button
                            row
                            variant="success"
                            onClick={onRerun}
                        >
                            {lang.rerun}
                        </Button>
                    </>
                )
            }

            <ConfirmWrapper
                show={showDelete}
                onClose={onCancelDelete}
            >
                <Confirm>
                    <ConfirmTitle>{activeRevision && activeRevision.name}</ConfirmTitle>
                    <ConfirmDescription>{lang.confirmDeleteRevision}</ConfirmDescription>
                    <ConfirmBottom>
                        <Button row onClick={onCancelDelete}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmDelete}>{lang.confirm}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>

            <ConfirmWrapper
                show={showRename}
                onClose={onCancelRename}
            >
                <Confirm>
                    <ConfirmTitle>{lang.renameRevision}</ConfirmTitle>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.name}</Label>
                                <Input type="text" value={revisionName} onChange={(e) => setRevisionName(e.target.value)} />
                                <Feedback>{revisionValidation && revisionValidation.name}</Feedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <ConfirmBottom>
                        <Button row onClick={onCancelRename}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmRename}>
                            {renameState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </ItemRevisionWrapper>
    )
}
export default ReviewItemRevisionList
